import Image from "next/image";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import Heading from "atoms/Heading";
import Text from "atoms/Text";
import SidebarListItems from "../molecules/SidebarListItems";
import {
  getAuth,
  getCompanyId,
  getCurrentCompanySlug,
  setAuth,
  setCompanyId,
  setCompanyTimeZone,
} from "services/identity.service";
import {
  useDomoReportQuery
} from "components/analytics/useMutationHooks";
import SelectCompanySlideOver from "./SelectCompanySlideOver";
import {
  getPlatformUserRole,
  isCustomerSuccess,
  isPlatformUser,
  roleDefaultPage,
} from "lib/utils";
import {
  getCompaniesByUserId,
  getUserById,
  getUserByUserIdAndCompanyId,
} from "services/user.service";
import {
  PRIVILEGE_ACTION,
  PRIVILEGE_SLUG,
  quizComingSoonMessage,
} from "../constants";
import APIResponseToast, { useToast } from "molecules/APIResponseToast";
import JourneysDropdown from "components/admin/journey-settings/sidebar/JourneysDropdown";
import { useDispatch, useSelector } from "react-redux";
import { setCanCreateJourney, setPrivileges } from "@/redux/privileges";
import {
  useGetCompaniesByUserIdQuery,
  useGetCompaniesQuery,
} from "utils/api/baseSlice";
import { getCompanyAndRolesByUserIdCompanyId } from "services/company.service";
import { setSelectedCompany } from "@/redux/company-users";
import {
  buildPrivilegesMapFromRoles,
  getSessionPrivilegesMap,
  hasPrivilege,
  normalizePrivileges,
} from "services/permission.service";

const Sidebar = ({
  mainLogoSrc = "/images/1ConnectLogo.svg",
  mainLogoWidth = 56,
  mainLogoHeight = 17,
  mainLogoAlt = "",
  userDetails,
}) => {
  const { data: domoReportList } = useDomoReportQuery();
  const hasDomoAccess = domoReportList?.some(report => report.domoReportUserRole.length > 0) || false;
  const showDesignationFlag =
    process.env.NEXT_PUBLIC_SHOW_DESIGNATION === "true";
  const myLoader = ({ src }) => src;
  const customerSuccess = isCustomerSuccess(userDetails?.roles);
  const platformUser = isPlatformUser(userDetails?.roles);
  const platformUserRole = getPlatformUserRole(userDetails?.roles);
  const router = useRouter();
  const dispatch = useDispatch();

  const goToRoute = (str) => (e) => {
    e.preventDefault();
    const companyId = getCompanyId();
    const slug = getCurrentCompanySlug(userDetails);
    router.push({
      pathname: `/[company]${str}`,
      query:
        str === "/admin/mytasks"
          ? { company: slug, tab: "pending" }
          : { company: slug },
    });
  };

  const companyList = useSelector((state) => state?.companyUsers?.companies);
  const [companies, setCompanies] = useState(companyList || []);
  const [pageLoading, setPageLoading] = useState(true);
  const { data: userCompanies = {} } = useGetCompaniesByUserIdQuery(userDetails.id, {
    skip: customerSuccess,
  });
  const { data: allCompanies = {} } = useGetCompaniesQuery({
    getSignedUrls: true,
  }, {
    skip: !customerSuccess,
  });
  const companiesData = customerSuccess
    ? (allCompanies?.rows || [])
    : (userCompanies?.company || []);

  useEffect(() => {
    if (!companiesData.length) return;
    const companiesWithSignedImages = companiesData.map((a) => ({ ...a }));
    companiesWithSignedImages.sort((a, b) =>
      a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase())
    );
    setCompanies(companiesWithSignedImages);
    setPageLoading(false);
  }, [companiesData]);

  const currentCompanyId = getCompanyId();
  const [currentCompanyDetails, setCurrentCompanyDetails] = useState({});
  const { toast, showToast } = useToast();

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      const auth = getAuth();
      if (!currentCompanyId || currentCompanyId==='null') {
        if (!customerSuccess) {
          setCurrentCompanyDetails({});
          return;
        }
      }
      if (platformUser) {
        const selectedCompany = companies.find((company) => company.id === currentCompanyId)
          || companies.find((company) => company.slug === router.query.company);
        if (selectedCompany) {
          if (selectedCompany.id !== currentCompanyId) {
            setCompanyId(selectedCompany.id);
            setCompanyTimeZone(selectedCompany.timeZone);
          }
          setCurrentCompanyDetails(selectedCompany);
          setPageLoading(false);
        }
        return;
      }
      if (auth?.id && currentCompanyId) {
        try {
          const currentCompany = await getCompanyAndRolesByUserIdCompanyId(
            auth?.id,
            currentCompanyId
          );
          if (currentCompany?.status && currentCompany?.entity?.token) {
            const latestAuth = getAuth();
            if (latestAuth?.token !== currentCompany?.entity?.token) {
              setAuth({ ...latestAuth, token: currentCompany?.entity?.token });
            }
          }
          // Drop token from UI state — it is only for AUTH cookie.
          const { token, ...companyDetails } = currentCompany?.entity;
          setCurrentCompanyDetails(companyDetails);
        } catch (error) {
          showToast({
            message: 'Error while fetching company details',
            type: 'error',
          });
          console.error('Error fetching company details', error);
        }
      }
    };

    fetchCompanyDetails();
  }, [companies, currentCompanyId, customerSuccess]); // Keep CS details in sync with all-company response.

  const [isSelectCompanySlideOver, setSelectCompanySlideOver] = useState(false);

  const onClickSuperAdmin = () => {
    setCompanyId(null);
    setCompanyTimeZone(null);
    setSelectCompanySlideOver(false);
    router.replace("/superadmin");
  };
  const onCompanySelect = async (company) => {
    setCompanyId(company.id);
    setCompanyTimeZone(company.timeZone);
    const auth = getAuth();
    // Platform users (CS / Super Admin) have no company membership rows;
    // keep existing JWT and resolve company from the companies list.
    if (!platformUser && auth?.id) {
      try {
        const res = await getCompanyAndRolesByUserIdCompanyId(auth?.id, company?.id);
        if (res?.status && res?.entity) {
          if (res?.entity?.token) {
            setAuth({ ...auth, token: res?.entity?.token });
          }
          const { token, ...companyDetails } = res?.entity;
          setCurrentCompanyDetails(companyDetails);
        }
      } catch (error) {
        showToast({
          message: 'Error while switching company',
          type: 'error',
        });
        console.error('Error switching company', error);
        return;
      }
    }
    const roles = platformUser
      ? userDetails?.roles
      : company?.userRoles?.map((item) => item?.Role);
  
    const routerPathObj = roleDefaultPage(roles, company);
    setSelectCompanySlideOver(false);
    router.push(routerPathObj);    
    dispatch(setSelectedCompany({ selectedCompany: company }));
  };

  const privileges = useMemo(() => {
    // Platform users keep session privileges. Company users use only the active
    // company's roles so tabs match the company-scoped JWT.
    if (platformUser) {
      return getSessionPrivilegesMap(userDetails);
    }
    return normalizePrivileges(
      buildPrivilegesMapFromRoles(currentCompanyDetails?.userRoles)
    );
  }, [currentCompanyDetails, userDetails, platformUser]);

  const canCreateJourney = useMemo(
    () =>
      hasPrivilege(
        privileges,
        PRIVILEGE_SLUG.campaignTemplates,
        PRIVILEGE_ACTION.CREATE
      ),
    [privileges]
  );

  useEffect(() => {
    dispatch(setPrivileges(privileges));
    dispatch(setCanCreateJourney(canCreateJourney));
  }, [canCreateJourney, privileges]);


  const getActiveMenu = (slug) => {
    return (
      router &&
      (router.pathname.includes(`/admin/${slug}`) || router.query.path === slug)
    );
  };

  const showOnlyHeader = userDetails?.showOnlyHeader;
  const getCompleteURL = (endpoint) => {
    const slug = getCurrentCompanySlug(
      userDetails,
      companies?.length ? companies : userDetails?.company
    );
    if (endpoint.includes("mytasks")) {
      endpoint = endpoint + "?tab=pending";
    }
    return `/${slug}/${endpoint}`;
  };
  const handleClick = () => {
      showToast({
        message: quizComingSoonMessage,
        type: "success",
      })
    }
  return (
    <div className="h-screen w-64 px-3 pt-6 fixed left-0 top-0 z-40 flex flex-col overflow-hidden">
      {/* Background Image */}
      {!showOnlyHeader && (
        <div className="absolute inset-0 -z-10">
          <Image
            src="/images/Sidebar-green.webp"
            width={256}
            height={1080}
            alt="Sidebar background"
            priority
            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
          />
        </div>
      )}
      <div
        className="flex justify-between items-center px-4 pb-4 mb-7 border-b border-gray-100 cursor-pointer"
        onClick={() => setSelectCompanySlideOver(true)}
      >
        <div className="flex items-center">
          <div className="flex items-center mr-2.5">
          {pageLoading || !currentCompanyDetails ? (
            <div className="w-[161px] h-[75px] bg-gray-200 animate-pulse"          />
          ) : (
           <div className="relative w-[161px] h-[75px]">
            <Image
              id="img_tag_126"
              loader={myLoader}
              src={currentCompanyDetails?.logo}
              alt={currentCompanyDetails?.companyName}
              fill
              className="object-contain"
              placeholder="blur"
              blurDataURL="/images/f-logo-new.svg"
            />
            </div>
          )}
          </div>
          {/* <Heading type="h4">{currentCompanyDetails?.companyName}</Heading> */}
        </div>
        {!showOnlyHeader && (
          <div className="shrink-0 ml-2">
            <Image
              id="img_tag_127"
              src="/images/icons/downAngle-gray.svg"
              width="10"
              height="10"
              alt="Arrow"
            />
          </div>
        )}
      </div>
      {!showOnlyHeader && (
        <ul className="overflow-y-auto flex-1">
          {hasPrivilege(privileges, [
            PRIVILEGE_SLUG.dashboard,
            PRIVILEGE_SLUG.reports,
          ]) && (
            <SidebarListItems
              menuItem="Dashboard"
              menuIconNormalSrc="/images/icons/home.svg"
              menuIconHoverSrc="/images/icons/home-o.svg"
              menuIconAlt="Dashboard"
              menuActive={getActiveMenu("dashboard")}
              href={getCompleteURL("admin/dashboard")}
              onClick={goToRoute("/admin/dashboard")}
            />
          )}
          {/* [MY TASKS - TEMPORARILY DISCONTINUED] The "My Tasks" nav item is hidden from the sidebar for all users.
              The underlying pages and edit flow remain intact and accessible via direct links from candidate timelines.
              To restore, uncomment this block. */}
          {/* {hasPrivilege(privileges, [
            PRIVILEGE_SLUG.tasks,
            PRIVILEGE_SLUG.allTasks,
          ]) && (
            <SidebarListItems
              menuItem="My Tasks"
              menuIconNormalSrc="/images/icons/calender-task.svg"
              menuIconHoverSrc="/images/icons/calender-task-o.svg"
              menuIconAlt="Candidates"
              menuActive={getActiveMenu("mytasks")}
              href={getCompleteURL("admin/mytasks")}
              onClick={goToRoute("/admin/mytasks")}
            />
          )} */}
          {hasPrivilege(privileges, PRIVILEGE_SLUG.candidates) && (
            <SidebarListItems
              menuItem="Candidates"
              menuIconNormalSrc="/images/icons/candidate-search.svg"
              menuIconHoverSrc="/images/icons/candidate-search-o.svg"
              menuIconAlt="Candidates"
              menuActive={getActiveMenu("candidates")}
              href={getCompleteURL("admin/candidates")}
              onClick={goToRoute("/admin/candidates")}
            />
          )}
          {hasPrivilege(privileges, PRIVILEGE_SLUG.employees) &&
            currentCompanyDetails?.isPostJoiningEnabled && (
            <SidebarListItems
              menuItem="Employees"
              menuIconNormalSrc="/images/icons/briefcase.svg"
              menuIconHoverSrc="/images/icons/briefcase-o.svg"
              menuIconAlt="Candidates"
              menuActive={getActiveMenu("employees")}
              href={getCompleteURL("admin/employees")}
              onClick={goToRoute("/admin/employees")}
            />
          )}
          {hasPrivilege(privileges, PRIVILEGE_SLUG.positions) && (
            <SidebarListItems
              menuItem="Positions"
              menuIconNormalSrc="/images/icons/chair.svg"
              menuIconHoverSrc="/images/icons/chair-o.svg"
              menuIconAlt="Positions"
              menuActive={getActiveMenu("positions")}
              href={getCompleteURL("admin/positions")}
              onClick={goToRoute("/admin/positions")}
            />
          )}
          {/* {privileges.campaign_templates && (
            <SidebarListItems
              menuItem="Campaign Sequence"
              menuIconNormalSrc="/images/icons/speaker.svg"
              menuIconHoverSrc="/images/icons/speaker-o.svg"
              menuIconAlt="Campaign Templates"
              menuActive={getActiveMenu("campaign-templates")}
              href={getCompleteURL("admin/campaign-templates")}
              onClick={goToRoute("/admin/campaign-templates")}
            />
          )} */}
         {hasPrivilege(privileges, PRIVILEGE_SLUG.campaignTemplates) &&
            hasPrivilege(privileges, PRIVILEGE_SLUG.htmlTemplates) && (
          <SidebarListItems
              menuItem={ <JourneysDropdown userDetails= {userDetails} menuActive={getActiveMenu()}/>}
              isIconAvail={false}
            />
         )} 
          {hasPrivilege(privileges, PRIVILEGE_SLUG.designations) && showDesignationFlag && (
            <SidebarListItems
              menuItem="Designations"
              menuIconNormalSrc="/images/icons/briefcase.svg"
              menuIconHoverSrc="/images/icons/briefcase-o.svg"
              menuIconAlt="Designations"
              menuActive={getActiveMenu("designations")}
              href={getCompleteURL("admin/designations")}
              onClick={goToRoute("/admin/designations")}
            />
          )}
          {hasPrivilege(privileges, PRIVILEGE_SLUG.newsFeed) && (
            <SidebarListItems
              menuItem="News Feed"
              menuIconNormalSrc="/images/icons/globe-gray.svg"
              menuIconHoverSrc="/images/icons/globe-o.svg"
              menuIconAlt="News Feed"
              menuActive={getActiveMenu("news-feeds")}
              href={getCompleteURL("admin/news-feeds")}
              onClick={goToRoute("/admin/news-feeds")}
            />
          )}
          {/* {privileges.html_templates && (
            <SidebarListItems
              menuItem="Content Templates"
              menuIconNormalSrc="/images/icons/code.svg"
              menuIconHoverSrc="/images/icons/code.svg"
              menuIconAlt="Content Templates"
              menuActive={getActiveMenu("html-templates")}
              href={getCompleteURL("admin/html-templates")}
              onClick={goToRoute("/admin/html-templates")}
            />
          )} */}
          {/* {privileges.survey_templates && (
            <SidebarListItems
              menuItem="Survey Templates"
              menuIconNormalSrc="/images/icons/survey.svg"
              menuIconHoverSrc="/images/icons/survey.svg"
              menuActive={getActiveMenu("survey-templates")}
              menuIconAlt="Survey Templates"
              href={getCompleteURL("admin/survey-templates")}
              onClick={goToRoute("/admin/survey-templates")}
            />
          )} */}

          {/* {privileges.quiz_templates && (
            <SidebarListItems
              menuItem="Quiz Templates (Coming Soon)"
              menuIconNormalSrc="/images/icons/question-mark.svg"
              menuIconHoverSrc="/images/icons/question-mark.svg"
              menuIconAlt="Quiz Templates (Coming Soon)"
              menuActive={getActiveMenu("quiz")}
              href={getCompleteURL("admin/quiz")}
              onClick={(e) => {
               e.preventDefault(); 
                 handleClick(); 
              }}
          />)} */}
          <APIResponseToast toast={toast} />

          {hasPrivilege(privileges, PRIVILEGE_SLUG.userManagement) && (
            <SidebarListItems
              menuItem="User Management"
              menuIconNormalSrc="/images/icons/users.svg"
              menuIconHoverSrc="/images/icons/users-o.svg"
              menuIconAlt="User Management"
              menuActive={getActiveMenu("users")}
              href={getCompleteURL("admin/users")}
              onClick={goToRoute("/admin/users")}
            />
          )}
          {hasPrivilege(
            privileges,
            PRIVILEGE_SLUG.themes,
            PRIVILEGE_ACTION.CREATE
          ) && (
            <SidebarListItems
              menuItem="Themes"
              menuIconNormalSrc="/images/icons/theme.svg"
              menuIconHoverSrc="/images/icons/theme-o.svg"
              menuIconAlt="Themes"
              menuActive={getActiveMenu("themes")}
              href={getCompleteURL("admin/themes")}
              onClick={goToRoute("/admin/themes")}
            />
          )}
          {hasPrivilege(privileges, PRIVILEGE_SLUG.settings) && (
            <SidebarListItems
              menuItem="Settings"
              menuIconNormalSrc="/images/icons/setting.svg"
              menuIconHoverSrc="/images/icons/setting-o.svg"
              menuIconAlt="Settings"
              menuActive={getActiveMenu("settings")}
              href={getCompleteURL("admin/settings")}
              onClick={goToRoute("/admin/settings")}
            />
          )}
          
          {(hasPrivilege(privileges, PRIVILEGE_SLUG.analytics) || hasDomoAccess) &&
           (<SidebarListItems
              menuItem="Analytics"
              menuIconNormalSrc="/images/icons/analytics.svg"
              menuIconHoverSrc="/images/icons/analytics-o.svg"
              menuIconAlt="Analytics"
              menuActive={getActiveMenu("analytics")}
              href={getCompleteURL("admin/analytics")}
              onClick={goToRoute("/admin/analytics")}
            />) }

          
        </ul>
      )}
      <div className="flex justify-center p-5 mt-auto">
        <Text className="text-xxs shrink-0 mr-1 text-white"> Powered By</Text>
        <Image
          id="img_tag_128"
          loader={myLoader}
          src={mainLogoSrc}
          width={mainLogoWidth}
          height={mainLogoHeight}
          alt={mainLogoAlt}
        />
      </div>
      <SelectCompanySlideOver
        companyList={companies}
        isPlatformUser={platformUser}
        platformUserRole={platformUserRole}
        onClickSuperAdmin={onClickSuperAdmin}
        isOpen={isSelectCompanySlideOver}
        onClose={() => setSelectCompanySlideOver(false)}
        onCompanySelect={onCompanySelect}
      />
    </div>
  );
};
export default Sidebar;

Sidebar.propTypes = {
  clientLogoSrc: PropTypes.string,
  clientLogoWidth: PropTypes.number,
  clientLogoHeight: PropTypes.number,
  clientLogoAlt: PropTypes.string,
  mainLogoSrc: PropTypes.string,
  mainLogoWidth: PropTypes.number,
  mainLogoHeight: PropTypes.number,
  mainLogoAlt: PropTypes.string,
  companyLists: PropTypes.array,
};